import { DateField } from "components/DesignSystem/DateInput/DateField";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { SwitchField } from "components/DesignSystem/Switch/SwitchField";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { GroupSelector } from "components/GroupSelector/GroupSelector";
import { AIOutlinedIcon } from "components/icons/AIOutlinedIcon";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { PriceInput } from "components/PriceInput/PriceInput";
import { BOOKS, MAILROOM, SALES_TAX, TAX } from "constants/apps";
import { INKLE_TEAM_AND_CPA_TEAM } from "constants/chatType";
import { YYYY_MM_DD } from "constants/date";
import {
  REMINDER_OPTIONS,
  REMINDER_OPTIONS_LIST,
  TICKET_PRIORITY_OPTIONS,
} from "constants/ticket";
import dayjs from "dayjs";
import { Form, Formik, useFormikContext } from "formik";
import { createNonChatTicketSchema } from "formValidations/ticketSchema";
import { useAuth } from "hooks/useAuth";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import { useSelector } from "react-redux";
import { MultiValue, SingleValue } from "react-select";
import NonPricedTicketTag from "static/images/NonPricedTicketTag.svg";
import PricedTicketTag from "static/images/PricedTicketTag.svg";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import {
  useCreateChatTicketMutation,
  useGetTicketChatDefaultsQuery,
} from "store/apis/chatTicket";
import { useGetGroupDataQuery } from "store/apis/group";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { RootState } from "store/store";
import { BackendError } from "types/utils/error";

export type TicketPayload = {
  ticket_title: string;
  company_group_id: string;
  group_entity_id: string;
  is_priced_ticket: "PRICED_TICKETS" | "UNPRICED_TICKETS";
  price: number;
  enablePayment: boolean;
  tags: string[];
  assignees: string[];
  createChatChannel: boolean;
  channelName: string;
  isNonChatTicket: boolean;
  estimatedDeliveryTime: string;
  reminderDate: string;
  canNotDeclare: boolean;
  customDate?: string;
  priority: string;
  isInternalTicket: boolean;
  description: string;
};

type CreateTicketProps = {
  isNonChatTicket: boolean;
  show: boolean;
  closeModal: () => void;
  currentEntityUuid?: string;
  groupId?: string;
  channelId?: string;
  taskId?: string;
  chatContext?: string;
};

type PrefillAsyncDefaultsProps = {
  title?: string;
  accountManagerProfileId?: string;
  groupId?: string;
  currentEntityUuid?: string;
};

const PrefillAsyncDefaults = ({
  title,
  accountManagerProfileId,
  groupId,
  currentEntityUuid,
}: PrefillAsyncDefaultsProps) => {
  const { touched, setFieldValue } = useTypedFormikContext<TicketPayload>();
  const isTitleTouched = Boolean(touched.ticket_title);
  const isAssigneesTouched = Boolean(touched.assignees);

  useEffect(() => {
    if (title && !isTitleTouched) setFieldValue("ticket_title", title);
  }, [title, isTitleTouched, setFieldValue]);

  useEffect(() => {
    if (accountManagerProfileId && !isAssigneesTouched) {
      setFieldValue("assignees", [accountManagerProfileId]);
    }
  }, [accountManagerProfileId, isAssigneesTouched, setFieldValue]);

  useEffect(() => {
    if (groupId) setFieldValue("company_group_id", groupId);
  }, [groupId, setFieldValue]);

  useEffect(() => {
    if (currentEntityUuid) setFieldValue("group_entity_id", currentEntityUuid);
  }, [currentEntityUuid, setFieldValue]);

  return null;
};

const GroupAndEntitySelector = ({
  isGroupExist,
}: {
  isGroupExist: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<TicketPayload>();
  const { company_group_id } = values;

  const { data, isFetching } = useGetGroupDataQuery(
    {
      groupId: company_group_id,
    },
    { skip: !company_group_id, refetchOnMountOrArgChange: true }
  );

  const { entities = [] } = data || {};

  return (
    <>
      {!isGroupExist && (
        <GroupSelector
          label="Select group"
          menuPortalTarget={document.body}
          maxMenuHeight={250}
          name="company_group_id"
          isClearable={false}
          required
          withForm
          onChange={() => {
            setFieldValue("group_entity_id", "");
          }}
        />
      )}

      <Combobox
        key={company_group_id}
        required
        isLoading={isFetching}
        isDisabled={!company_group_id || isFetching}
        menuPortalTarget={document.body}
        label="Select entity"
        withForm
        name="group_entity_id"
        options={entities?.map((e) => ({
          label: (
            <div className="t-flex t-gap-2 t-items-center">
              <ReactCountryFlag countryCode={e.country_code} svg />
              {e.name}
            </div>
          ),
          value: e.uuid,
        }))}
      />
    </>
  );
};

export const CreateTicket = ({
  isNonChatTicket,
  show,
  closeModal,
  currentEntityUuid,
  groupId,
  channelId,
  taskId,
  chatContext,
}: CreateTicketProps) => {
  const { isCustomer, canManageInternalTickets } = useRoleBasedView();
  const [createNonChatTicket] = useCreateChatTicketMutation();
  const { alertToast, successToast } = useToast();
  const user = useAuth();
  const today = dayjs().startOf("day");

  const { data: tagsData = [] } = useGetTagsForCategoryQuery({
    tagType: "TICKETS_TAG",
  });

  const { data: assigneesData = [] } = useGetServiceTeamQuery(
    {
      accessible_teams: canManageInternalTickets
        ? INKLE_TEAM_AND_CPA_TEAM
        : null,
    },
    {
      skip: isCustomer,
    }
  );

  const { messageId, isInternalTicket } = useSelector(
    (state: RootState) => state.ticket.createTicketData
  );

  const { data: groupData } = useGetGroupDataQuery(
    { groupId: groupId || "" },
    { skip: !groupId || !isInternalTicket }
  );
  const accountManagerProfileId = groupData?.assigned_agent?.profile_id;

  const { data: chatDefaults, isLoading: isChatDefaultsLoading } =
    useGetTicketChatDefaultsQuery(
      { company_group_id: groupId || "", chat_context: chatContext },
      { skip: !isInternalTicket || !groupId }
    );

  const assigneeOptions = assigneesData.map(({ name, uuid }) => ({
    label: name,
    value: uuid,
  }));

  const onSubmit = async ({
    assignees,
    company_group_id,
    group_entity_id,
    is_priced_ticket,
    price,
    tags,
    ticket_title,
    enablePayment,
    createChatChannel,
    channelName,
    isNonChatTicket,
    estimatedDeliveryTime,
    reminderDate,
    customDate,
    priority,
    description,
  }: TicketPayload) => {
    try {
      const isPricedTicket = is_priced_ticket === "PRICED_TICKETS";
      const reminder = {
        reminder_date: (reminderDate === REMINDER_OPTIONS.CUSTOM
          ? dayjs(customDate)
          : today.add(Number(reminderDate), "day")
        ).format(YYYY_MM_DD),
      };
      const etdData = estimatedDeliveryTime
        ? { etd_date: dayjs(estimatedDeliveryTime).format(YYYY_MM_DD) }
        : reminder;

      await createNonChatTicket({
        ticket_title,
        company_group_id,
        group_entity_id: group_entity_id || "",
        is_priced_ticket: isPricedTicket,
        is_price_finalized: enablePayment,
        ticket_price: isPricedTicket ? price : 0,
        tag_ids: tags.join(","),
        assignee_profile_ids: assignees.join(","),
        is_non_chat_ticket: isNonChatTicket || false,
        channel_url: isNonChatTicket ? "" : channelId || "",
        message_id: isNonChatTicket ? "" : messageId,
        create_ticket_channel: createChatChannel,
        ticket_channel_name: channelName,
        task_id: taskId,
        priority: priority,
        is_internal_ticket: isInternalTicket,
        description: description || undefined,
        ...etdData,
      }).unwrap();
      successToast({ message: "Ticket created successfully" });
      closeModal();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleReminderDateChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!option || option instanceof Array) {
      return;
    }

    if (option.value !== REMINDER_OPTIONS.CUSTOM) {
      setFieldValue("customDate", "");
    }

    setFieldValue("reminderDate", option.value);
  };

  const handleCanNotDeclareChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (e.target.checked) {
      setFieldValue("estimatedDeliveryTime", "");
    } else {
      setFieldValue("reminderDate", "");
    }
    setFieldValue("canNotDeclare", e.target.checked);
  };

  const ticketTypeOptions = [
    {
      name: "Unpriced tickets",
      img: NonPricedTicketTag,
      value: "UNPRICED_TICKETS",
    },
    { name: "Priced tickets", img: PricedTicketTag, value: "PRICED_TICKETS" },
  ].map(({ name, img, value }) => ({
    label: (
      <div className="t-flex t-items-center t-gap-2">
        <img src={img} alt="PricedTicketTag" />
        {name}
      </div>
    ),
    value,
  }));

  return (
    <Modal.Root open={show} onOpenChange={closeModal} modal={false}>
      <Formik
        initialValues={{
          isNonChatTicket,
          ticket_title: chatDefaults?.title || "",
          company_group_id: groupId || "",
          group_entity_id: currentEntityUuid || "",
          is_priced_ticket: "UNPRICED_TICKETS",
          price: 0,
          enablePayment: false,
          tags: [],
          assignees:
            isInternalTicket && accountManagerProfileId
              ? [accountManagerProfileId]
              : [user.uuid],
          createChatChannel: false,
          channelName: "",
          estimatedDeliveryTime: isInternalTicket
            ? dayjs().add(12, "hour").format(YYYY_MM_DD)
            : "",
          reminderDate: "",
          canNotDeclare: false,
          customDate: "",
          priority: "HIGH",
          isInternalTicket,
          description: "",
        }}
        onSubmit={onSubmit}
        validationSchema={createNonChatTicketSchema}
        validateOnChange
      >
        {({
          isSubmitting,
          values: {
            is_priced_ticket,
            createChatChannel,
            isNonChatTicket,
            canNotDeclare,
            reminderDate,
            assignees,
          },
          submitForm,
          setFieldValue,
        }) => (
          <Modal.Content useCustomOverlay>
            <Modal.Header>
              <Modal.Title>
                {isInternalTicket ? "Create Internal Ticket" : "Create Ticket"}
              </Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <Form className="t-flex t-flex-col t-gap-5 t-m-0 t-w-full">
                <PrefillAsyncDefaults
                  title={chatDefaults?.title}
                  accountManagerProfileId={accountManagerProfileId}
                  groupId={groupId}
                  currentEntityUuid={currentEntityUuid}
                />
                {isInternalTicket && isChatDefaultsLoading ? (
                  <div className="t-flex t-flex-col t-gap-1">
                    <Label required>Title</Label>
                    <Skeleton className="t-h-10 t-w-full" />
                  </div>
                ) : (
                  <>
                    <TextInput
                      required
                      block
                      label="Title"
                      name="ticket_title"
                      placeholder="Add title"
                    />
                    {isInternalTicket && chatDefaults?.title && (
                      <span className="t-text-text-30 t-text-[11px] t-italic -t-mt-3 t-flex t-items-center t-gap-1">
                        <AIOutlinedIcon />
                        Generated by AI
                      </span>
                    )}
                    {isInternalTicket && (
                      <TextArea
                        required
                        block
                        label="Description"
                        name="description"
                        placeholder="Add context and background for this ticket"
                      />
                    )}
                  </>
                )}
                {!isInternalTicket && (
                  <Combobox
                    label="Ticket type"
                    required
                    menuPortalTarget={document.body}
                    withForm
                    name="is_priced_ticket"
                    isMulti={false}
                    isClearable={false}
                    defaultValue={ticketTypeOptions[0]}
                    options={ticketTypeOptions}
                    onChange={(value) => {
                      setFieldValue("price", 0);
                      setFieldValue(
                        "enablePayment",
                        (value as SingleValue<OptionData>)?.value ===
                          "PRICED_TICKETS"
                      );
                    }}
                  />
                )}
                {!currentEntityUuid && (
                  <GroupAndEntitySelector isGroupExist={Boolean(groupId)} />
                )}
                {!isInternalTicket && (
                  <Combobox
                    label="Select priority"
                    menuPlacement="bottom"
                    menuPortalTarget={document.body}
                    withForm
                    required
                    components={{
                      ClearIndicator: () => null,
                    }}
                    name="priority"
                    options={TICKET_PRIORITY_OPTIONS}
                    defaultValue={TICKET_PRIORITY_OPTIONS[1]}
                  />
                )}
                <DateField
                  name="estimatedDeliveryTime"
                  dateProps={{
                    label: "Estimated Time of Delivery (ETD)",
                    required: !canNotDeclare,
                    disabled: canNotDeclare,
                    onDateChange: (date: Date) =>
                      setFieldValue("estimatedDeliveryTime", date),
                    minDate: today.toDate(),
                    rightComponent: (
                      <span className="t-text-text-30 -t-mt-0.5">
                        <CalendarIcon />
                      </span>
                    ),
                  }}
                />
                <div className="-t-mt-3">
                  <Checkbox
                    name="canNotDeclare"
                    label="I can’t declare an ETD yet, schedule a reminder to add it later"
                    onChange={(e) =>
                      handleCanNotDeclareChange(e, setFieldValue)
                    }
                  />
                </div>
                {canNotDeclare && (
                  <Combobox
                    label="Remind me to add an ETD in"
                    menuPlacement="bottom"
                    menuPortalTarget={document.body}
                    withForm
                    name="reminderDate"
                    options={REMINDER_OPTIONS_LIST}
                    onChange={(option) =>
                      handleReminderDateChange(option, setFieldValue)
                    }
                    required
                  />
                )}
                {reminderDate === REMINDER_OPTIONS.CUSTOM && (
                  <DateField
                    name="customDate"
                    dateProps={{
                      label: "Custom date",
                      required: true,
                      onDateChange: (date: Date) =>
                        setFieldValue("customDate", date),
                      minDate: today.toDate(),
                      rightComponent: (
                        <span className="t-text-text-30 -t-mt-0.5">
                          <CalendarIcon />
                        </span>
                      ),
                    }}
                  />
                )}

                {assigneesData.length > 0 && (
                  <Combobox
                    required
                    label="Assigned to"
                    menuPlacement="top"
                    menuPortalTarget={document.body}
                    withForm
                    isMulti
                    isSearchable
                    isClearable
                    name="assignees"
                    options={assigneeOptions}
                    value={assigneeOptions.filter(({ value }) =>
                      assignees.includes(value)
                    )}
                  />
                )}

                {!isInternalTicket &&
                  Boolean(is_priced_ticket === "PRICED_TICKETS") && (
                    <>
                      <PriceInput
                        required
                        name="price"
                        label="Amount"
                        allowNegative={false}
                      />

                      <SwitchField
                        name="enablePayment"
                        label="Enable invoice and ask for payment"
                        switchSide="left"
                      />
                    </>
                  )}

                <Combobox
                  label="Tags"
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  withForm
                  isMulti
                  isSearchable
                  isClearable
                  name="tags"
                  options={tagsData.map(({ title, uuid }) => ({
                    label: title,
                    value: uuid,
                  }))}
                />

                {Boolean(isNonChatTicket) && !isInternalTicket && (
                  <>
                    <SwitchField
                      label="Create chat channel for this ticket"
                      name="createChatChannel"
                      onChange={() => setFieldValue("channelName", "")}
                    />
                    {Boolean(createChatChannel) && (
                      <TextInput
                        required
                        autoFocus
                        block
                        label="Channel name"
                        name="channelName"
                        placeholder="Enter the channel name"
                      />
                    )}
                  </>
                )}
              </Form>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Button
                customType="secondary"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                customType="primary"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={submitForm}
              >
                Create
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        )}
      </Formik>
    </Modal.Root>
  );
};
